import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiEdit2, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import chatSupportLogService from "../../api/services/chatSupportLogService";
import ConfirmWarningModal from "../../components/ConfirmWarningModal";
import { notifyApprovalsPendingChanged } from "../../utils/approvalsEvents";
import {
  PAGE_SIZE,
  PageAccessSkeleton,
  TablePagination,
  TableSkeleton,
  usePagedList,
} from "./chatSupportLogUi";

const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none";

const ALLOWED = ["Admin", "Developer", "Chat Support", "CSR Team Lead"];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatLogDate(value) {
  if (!value) return "—";
  if (typeof value === "string") return value.slice(0, 10);
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return String(value);
  }
}

function formatMinutes(mins) {
  const n = Number(mins) || 0;
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

const emptyActivityForm = () => ({
  client_name: "",
  client_email: "",
  client_phone: "",
  channel: "chat",
  time_spent_minutes: "",
  notes: "",
});

export default function ChatSupportActivityPage() {
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const isAllowed = ALLOWED.includes(userRole);
  const isDeveloper = userRole === "Developer";
  const isAdmin = userRole === "Admin";
  const isSupervisor = isAdmin || isDeveloper;
  const isChatSupport = userRole === "Chat Support";
  const isTeamLead = userRole === "CSR Team Lead";

  const [access, setAccess] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activityTotals, setActivityTotals] = useState({ chat: 0, email: 0, social: 0, random: 0 });
  const [targets, setTargets] = useState({
    chatMessages: 0,
    emailsAdded: 0,
    socialResponses: 0,
    randomRequests: 0,
  });
  const [activityModal, setActivityModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const isViewOnly = Boolean(access?.isViewOnly) || isDeveloper;
  const canEditActivity =
    (Boolean(access?.canEditLogs) || isChatSupport || isAdmin || isTeamLead) && !isViewOnly;
  const canManageTeam = isTeamLead || isSupervisor;

  const loadAccess = useCallback(async () => {
    setAccessLoading(true);
    try {
      const data = await chatSupportLogService.getAccess();
      setAccess(data.access);
      if (data.access?.targets) setTargets(data.access.targets);
      if (!selectedAgentId && data.access?.agents?.length) {
        const mine = data.access.agents.find((a) => a.agentId === data.access.adminId);
        setSelectedAgentId(mine?.agentId || data.access.agents[0]?.agentId || data.access.adminId);
      } else if (!selectedAgentId && data.access?.adminId) {
        setSelectedAgentId(data.access.adminId);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load access");
    } finally {
      setAccessLoading(false);
    }
  }, [selectedAgentId]);

  const loadActivity = useCallback(async () => {
    const agentId = isChatSupport ? access?.adminId : selectedAgentId;
    if (!agentId) return;
    setLoading(true);
    try {
      const data = await chatSupportLogService.getActivityLogs({
        date: selectedDate,
        agent_id: agentId,
      });
      setActivities(data.activities || []);
      setActivityTotals(data.totals || { chat: 0, email: 0, social: 0, random: 0 });
      if (data.targets) setTargets(data.targets);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [access?.adminId, isChatSupport, selectedAgentId, selectedDate]);

  useEffect(() => {
    if (!isAllowed) return;
    loadAccess();
  }, [isAllowed, loadAccess]);

  useEffect(() => {
    if (!isAllowed || !access) return;
    loadActivity();
  }, [access, isAllowed, loadActivity]);

  const activitiesPaging = usePagedList(
    activities,
    `act-${selectedDate}-${selectedAgentId}`,
    PAGE_SIZE
  );

  const openCreateModal = () => {
    setActivityModal({ mode: "create", ...emptyActivityForm() });
  };

  const openEditModal = (row) => {
    setActivityModal({
      mode: "edit",
      id: row.id,
      client_name: row.clientName || "",
      client_email: row.clientEmail || "",
      client_phone: row.clientPhone || "",
      channel: row.channel || "chat",
      time_spent_minutes: String(row.timeSpentMinutes ?? ""),
      notes: row.notes || "",
    });
  };

  const saveActivity = async () => {
    if (!activityModal?.client_name?.trim()) {
      toast.error("User name is required");
      return;
    }
    if (!activityModal.channel) {
      toast.error("Channel is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        log_date: selectedDate,
        agent_id: isChatSupport ? access?.adminId : selectedAgentId || access?.adminId,
        client_name: activityModal.client_name.trim(),
        client_email: activityModal.client_email.trim(),
        client_phone: activityModal.client_phone.trim(),
        channel: activityModal.channel,
        time_spent_minutes: Number(activityModal.time_spent_minutes) || 0,
        notes: activityModal.notes,
        quantity: 1,
      };
      if (activityModal.mode === "edit") {
        await chatSupportLogService.updateActivityLog(activityModal.id, payload);
        toast.success("Record updated — sent for team lead approval");
      } else {
        await chatSupportLogService.createActivityLog(payload);
        toast.success("Record added — pending team lead approval");
      }
      setActivityModal(null);
      loadActivity();
      notifyApprovalsPendingChanged({ delta: 1 });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save record");
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteActivity = (row) => {
    setConfirmModal({
      title: "Delete activity record?",
      message: `Remove record for ${row.clientName || row.channel} (${row.channel})? Daily report counts will update.`,
      confirmLabel: "Delete",
      onConfirm: async () => {
        setSaving(true);
        try {
          await chatSupportLogService.deleteActivityLog(row.id);
          toast.success("Deleted — daily report synced");
          setConfirmModal(null);
          if (activityModal?.id === row.id) setActivityModal(null);
          loadActivity();
        } catch (e) {
          toast.error(e?.response?.data?.message || "Failed to delete");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  if (!isAllowed) {
    return <Navigate to="/dashboard" replace />;
  }

  if (accessLoading && !access) {
    return (
      <div className="p-6">
        <PageAccessSkeleton />
      </div>
    );
  }

  const tableCols =
    (canManageTeam ? 1 : 0) + 7 + (canEditActivity ? 1 : 0);

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chat / Email / Social</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manually add chat, email, social, and random client records. Each new record goes to the team lead for
            approval; approved records sync into the daily report.
            {canManageTeam && (
              <span className="ml-1">Team leads can add for any agent and review each member’s records.</span>
            )}
            {isViewOnly && (
              <span className="ml-1 text-violet-700 font-medium">View only — you can browse but cannot edit.</span>
            )}
          </p>
        </div>
        {canEditActivity && (
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ backgroundColor: THEME.primary }}
          >
            <FiPlus /> Add record
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className={inputCls}
          />
        </div>
        {canManageTeam && access?.agents?.length > 0 && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Agent</label>
            <select
              value={selectedAgentId || ""}
              onChange={(e) => setSelectedAgentId(Number(e.target.value))}
              className={inputCls}
            >
              {access.agents.map((a) => (
                <option key={a.agentId} value={a.agentId}>
                  {a.name || a.email}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { key: "chat", label: "Chat", value: activityTotals.chat, target: targets.chatMessages },
          { key: "email", label: "Email", value: activityTotals.email, target: targets.emailsAdded },
          { key: "social", label: "Social", value: activityTotals.social, target: targets.socialResponses },
          { key: "random", label: "Random", value: activityTotals.random, target: targets.randomRequests },
        ].map((c) => (
          <div
            key={c.key}
            className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex items-center justify-between"
          >
            <div>
              <p className="text-xs font-semibold text-gray-500">{c.label} total</p>
              <p className="text-xl font-bold text-gray-900">{c.value}</p>
            </div>
            <p className="text-xs text-gray-400">Target {c.target || 0}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-gray-900">Client records</h2>
          <span className="text-xs text-gray-400">{activities.length} entries</span>
        </div>
        {loading ? (
          <TableSkeleton rows={6} cols={tableCols} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {canManageTeam && (
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Agent</th>
                    )}
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">User name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Phone</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Channel</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Time spent</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">TL approval</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Notes</th>
                    {canEditActivity && (
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 w-24">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {activitiesPaging.pageItems.map((row) => (
                    <tr key={row.id} className="border-t border-gray-100">
                      {canManageTeam && (
                        <td className="px-3 py-2 text-gray-700">{row.agentName || row.agentEmail || "—"}</td>
                      )}
                      <td className="px-3 py-2 font-medium text-gray-900">{row.clientName || "—"}</td>
                      <td className="px-3 py-2 text-gray-600">{row.clientEmail || "—"}</td>
                      <td className="px-3 py-2 text-gray-600">{row.clientPhone || "—"}</td>
                      <td className="px-3 py-2 text-gray-900 capitalize">{row.channel}</td>
                      <td className="px-3 py-2 text-gray-700">{formatMinutes(row.timeSpentMinutes)}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                            row.tlApprovalStatus === "approved"
                              ? "bg-emerald-100 text-emerald-800"
                              : row.tlApprovalStatus === "rejected"
                                ? "bg-rose-100 text-rose-800"
                                : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {row.tlApprovalStatus || "pending"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate" title={row.notes || ""}>
                        {row.notes || "—"}
                      </td>
                      {canEditActivity && (
                        <td className="px-3 py-2 text-center">
                          <div className="flex justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEditModal(row)}
                              className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"
                              title="Edit"
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => requestDeleteActivity(row)}
                              className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"
                              title="Delete"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {!activities.length && (
                    <tr>
                      <td colSpan={tableCols} className="px-3 py-12 text-center text-gray-400">
                        No client records for this date.
                        {canEditActivity && <> Click <strong>Add record</strong> to log a client.</>}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <TablePagination paging={activitiesPaging} label="client records" />
          </>
        )}
      </div>

      {activityModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900">
                {activityModal.mode === "edit" ? "Edit client record" : "Add client record"}
              </h2>
              <button
                type="button"
                onClick={() => !saving && setActivityModal(null)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <FiX size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">
                Fill in the client you worked with on {formatLogDate(selectedDate)}
                {canManageTeam ? " for the selected agent" : ""}. The record goes to the team lead for approval
                before it counts on the daily report.
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">User name *</label>
                <input
                  type="text"
                  value={activityModal.client_name}
                  onChange={(e) => setActivityModal((m) => ({ ...m, client_name: e.target.value }))}
                  className={`${inputCls} w-full`}
                  placeholder="Client full name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
                  <input
                    type="email"
                    value={activityModal.client_email}
                    onChange={(e) => setActivityModal((m) => ({ ...m, client_email: e.target.value }))}
                    className={`${inputCls} w-full`}
                    placeholder="client@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Phone number</label>
                  <input
                    type="tel"
                    value={activityModal.client_phone}
                    onChange={(e) => setActivityModal((m) => ({ ...m, client_phone: e.target.value }))}
                    className={`${inputCls} w-full`}
                    placeholder="+92…"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Channel *</label>
                  <select
                    value={activityModal.channel}
                    onChange={(e) => setActivityModal((m) => ({ ...m, channel: e.target.value }))}
                    className={`${inputCls} w-full`}
                  >
                    <option value="chat">Chat</option>
                    <option value="email">Email</option>
                    <option value="social">Social media</option>
                    <option value="random">Random request</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    Time spent (minutes)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={activityModal.time_spent_minutes}
                    onChange={(e) =>
                      setActivityModal((m) => ({ ...m, time_spent_minutes: e.target.value }))
                    }
                    className={`${inputCls} w-full`}
                    placeholder="e.g. 25"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Notes</label>
                <textarea
                  value={activityModal.notes}
                  onChange={(e) => setActivityModal((m) => ({ ...m, notes: e.target.value }))}
                  className={`${inputCls} w-full min-h-[88px]`}
                  placeholder="What was discussed, follow-up, outcome…"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={() => setActivityModal(null)}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveActivity}
                disabled={saving}
                className="px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {saving ? "Saving…" : activityModal.mode === "edit" ? "Update record" : "Add record"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmWarningModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel || "Confirm"}
        variant="danger"
        confirming={saving}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
}
